'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type CountryCode = string;

export interface Country {
  code: CountryCode;
  name: string;
  name_ar: string;
  emoji: string;
  phone_prefix: string;
  currency_code?: string;
  currency_symbol?: string;
  vat_rate?: number | string;
  is_default?: boolean;
  is_active?: boolean;
  is_enabled?: boolean;
  payment_method_codes?: string[];
  delivery_method_ids?: number[];
  enabled_feature_codes?: string[];
  category_slugs?: string[];
  ad_ids?: number[];
  content_slugs?: string[];
  currency: string;
  prefix: string;
}

const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
const STORAGE_KEY = 'app_country_code';
const LEGACY_COUNTRY_KEY = 's360_country';
const COOKIE_KEY = 'app_country_code';
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

function normalizeCountry(raw: Partial<Country>): Country {
  const code = String(raw.code || '').trim().toUpperCase();
  const phonePrefix = String(raw.phone_prefix || raw.prefix || '').trim();
  const currencySymbol = String(raw.currency_symbol || raw.currency || raw.currency_code || '').trim();
  return {
    code,
    name: String(raw.name || code),
    name_ar: String(raw.name_ar || raw.name || code),
    emoji: String(raw.emoji || '🌍'),
    phone_prefix: phonePrefix,
    currency_code: raw.currency_code ? String(raw.currency_code) : '',
    currency_symbol: currencySymbol,
    vat_rate: raw.vat_rate,
    is_default: Boolean(raw.is_default),
    is_active: raw.is_active ?? true,
    is_enabled: raw.is_enabled ?? true,
    payment_method_codes: Array.isArray(raw.payment_method_codes) ? raw.payment_method_codes : [],
    delivery_method_ids: Array.isArray(raw.delivery_method_ids) ? raw.delivery_method_ids : [],
    enabled_feature_codes: Array.isArray(raw.enabled_feature_codes) ? raw.enabled_feature_codes : [],
    category_slugs: Array.isArray(raw.category_slugs) ? raw.category_slugs : [],
    ad_ids: Array.isArray(raw.ad_ids) ? raw.ad_ids : [],
    content_slugs: Array.isArray(raw.content_slugs) ? raw.content_slugs : [],
    currency: currencySymbol,
    prefix: phonePrefix.replace(/^\+/, ''),
  };
}

export const COUNTRIES: Country[] = [
  normalizeCountry({ code: 'OM', name: 'Oman', name_ar: 'عُمان', emoji: '🇴🇲', phone_prefix: '+968', currency_symbol: 'ر.ع', is_default: true }),
  normalizeCountry({ code: 'DZ', name: 'Algeria', name_ar: 'الجزائر', emoji: '🇩🇿', phone_prefix: '+213', currency_symbol: 'د.ج' }),
  normalizeCountry({ code: 'SA', name: 'Saudi Arabia', name_ar: 'السعودية', emoji: '🇸🇦', phone_prefix: '+966', currency_symbol: 'ر.س' }),
  normalizeCountry({ code: 'AE', name: 'UAE', name_ar: 'الإمارات', emoji: '🇦🇪', phone_prefix: '+971', currency_symbol: 'د.إ' }),
  normalizeCountry({ code: 'EG', name: 'Egypt', name_ar: 'مصر', emoji: '🇪🇬', phone_prefix: '+20', currency_symbol: 'ج.م' }),
  normalizeCountry({ code: 'MA', name: 'Morocco', name_ar: 'المغرب', emoji: '🇲🇦', phone_prefix: '+212', currency_symbol: 'د.م' }),
  normalizeCountry({ code: 'TN', name: 'Tunisia', name_ar: 'تونس', emoji: '🇹🇳', phone_prefix: '+216', currency_symbol: 'د.ت' }),
  normalizeCountry({ code: 'LY', name: 'Libya', name_ar: 'ليبيا', emoji: '🇱🇾', phone_prefix: '+218', currency_symbol: 'د.ل' }),
  normalizeCountry({ code: 'QA', name: 'Qatar', name_ar: 'قطر', emoji: '🇶🇦', phone_prefix: '+974', currency_symbol: 'ر.ق' }),
  normalizeCountry({ code: 'KW', name: 'Kuwait', name_ar: 'الكويت', emoji: '🇰🇼', phone_prefix: '+965', currency_symbol: 'د.ك' }),
  normalizeCountry({ code: 'BH', name: 'Bahrain', name_ar: 'البحرين', emoji: '🇧🇭', phone_prefix: '+973', currency_symbol: 'د.ب' }),
  normalizeCountry({ code: 'JO', name: 'Jordan', name_ar: 'الأردن', emoji: '🇯🇴', phone_prefix: '+962', currency_symbol: 'د.أ' }),
  normalizeCountry({ code: 'IQ', name: 'Iraq', name_ar: 'العراق', emoji: '🇮🇶', phone_prefix: '+964', currency_symbol: 'د.ع' }),
  normalizeCountry({ code: 'LB', name: 'Lebanon', name_ar: 'لبنان', emoji: '🇱🇧', phone_prefix: '+961', currency_symbol: 'ل.ل' }),
  normalizeCountry({ code: 'SY', name: 'Syria', name_ar: 'سوريا', emoji: '🇸🇾', phone_prefix: '+963', currency_symbol: 'ل.س' }),
  normalizeCountry({ code: 'SD', name: 'Sudan', name_ar: 'السودان', emoji: '🇸🇩', phone_prefix: '+249', currency_symbol: 'ج.س' }),
  normalizeCountry({ code: 'FR', name: 'France', name_ar: 'فرنسا', emoji: '🇫🇷', phone_prefix: '+33', currency_symbol: '€' }),
  normalizeCountry({ code: 'DE', name: 'Germany', name_ar: 'ألمانيا', emoji: '🇩🇪', phone_prefix: '+49', currency_symbol: '€' }),
  normalizeCountry({ code: 'GB', name: 'UK', name_ar: 'المملكة المتحدة', emoji: '🇬🇧', phone_prefix: '+44', currency_symbol: '£' }),
  normalizeCountry({ code: 'US', name: 'USA', name_ar: 'الولايات المتحدة', emoji: '🇺🇸', phone_prefix: '+1', currency_symbol: '$' }),
  normalizeCountry({ code: 'TR', name: 'Turkey', name_ar: 'تركيا', emoji: '🇹🇷', phone_prefix: '+90', currency_symbol: '₺' }),
];

interface CountryContextType {
  selectedCountryCode: CountryCode;
  setSelectedCountryCode: (code: CountryCode) => void;
  selectedCountry: Country | undefined;
  countries: Country[];
}

const CountryContext = createContext<CountryContextType | undefined>(undefined);

function readStoredCountryCode(): string {
  if (typeof window === 'undefined') return '';
  const primary = localStorage.getItem(STORAGE_KEY) || '';
  if (primary) return primary.trim().toUpperCase();
  const legacyRaw = localStorage.getItem(LEGACY_COUNTRY_KEY) || '';
  if (!legacyRaw) return '';
  try {
    const parsed = JSON.parse(legacyRaw);
    return String(parsed?.code || '').trim().toUpperCase();
  } catch {
    return '';
  }
}

function persistCountry(country: Country | undefined, code: string) {
  if (typeof window === 'undefined') return;
  const normalized = code.trim().toUpperCase();
  if (!normalized) return;
  localStorage.setItem(STORAGE_KEY, normalized);
  document.cookie = `${COOKIE_KEY}=${encodeURIComponent(normalized)}; path=/; max-age=${ONE_YEAR_SECONDS}; SameSite=Lax`;
  if (country) {
    localStorage.setItem(LEGACY_COUNTRY_KEY, JSON.stringify(country));
  }
}

function pickDetectedCountry(countries: Country[]): Country | undefined {
  if (typeof window === 'undefined') return undefined;

  const locale = navigator.language || '';
  const localeRegion = locale.split('-')[1]?.toUpperCase() || '';
  if (localeRegion) {
    const localeMatch = countries.find((country) => country.code === localeRegion);
    if (localeMatch) return localeMatch;
  }

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  const timeZoneMap: Record<string, string> = {
    Muscat: 'OM',
    Riyadh: 'SA',
    Dubai: 'AE',
    Qatar: 'QA',
    Kuwait: 'KW',
  };
  const matchedCode = Object.entries(timeZoneMap).find(([token]) => timeZone.includes(token))?.[1];
  if (!matchedCode) return undefined;
  return countries.find((country) => country.code === matchedCode);
}

function shouldAttachCountryHeader(url: string): boolean {
  if (!url) return false;
  if (url.startsWith('/api/')) return true;
  return url.startsWith(`${API}/api/`);
}

export function CountryProvider({ children }: { children: ReactNode }) {
  const [countries, setCountries] = useState<Country[]>(COUNTRIES);
  const [selectedCountryCode, setSelectedCountryCode] = useState<CountryCode>(
    COUNTRIES.find((country) => country.is_default)?.code || 'OM',
  );
  const selectedCountryCodeRef = useRef(selectedCountryCode);

  useEffect(() => {
    selectedCountryCodeRef.current = selectedCountryCode;
  }, [selectedCountryCode]);

  useEffect(() => {
    let cancelled = false;

    const applySelectedCountry = (availableCountries: Country[]) => {
      const safeCountries = availableCountries.length > 0 ? availableCountries : COUNTRIES;
      const savedCode = readStoredCountryCode();
      const saved = safeCountries.find((country) => country.code === savedCode);
      const detected = pickDetectedCountry(safeCountries);
      const fallback = safeCountries.find((country) => country.is_default) || safeCountries[0];
      const resolved = saved || detected || fallback;
      if (!resolved || cancelled) return;
      setSelectedCountryCode(resolved.code);
      persistCountry(resolved, resolved.code);
    };

    const loadCountries = async () => {
      try {
        const res = await fetch(`${API}/api/auth/countries/`, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('Failed to fetch countries');
        const payload = await res.json();
        if (!Array.isArray(payload) || payload.length === 0) {
          applySelectedCountry(COUNTRIES);
          return;
        }
        const remoteCountries = payload
          .map((row) => normalizeCountry(row))
          .filter((country) => country.code && country.is_active !== false && country.is_enabled !== false);
        if (!cancelled && remoteCountries.length > 0) {
          setCountries(remoteCountries);
        }
        applySelectedCountry(remoteCountries);
      } catch {
        if (!cancelled) {
          setCountries(COUNTRIES);
        }
        applySelectedCountry(COUNTRIES);
      }
    };

    loadCountries();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const originalFetch = window.fetch.bind(window);

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const requestUrl =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.toString()
            : input.url;

      if (!shouldAttachCountryHeader(requestUrl)) {
        return originalFetch(input, init);
      }

      const headers = new Headers(
        init?.headers || (!(input instanceof URL) && typeof input !== 'string' ? input.headers : undefined)
      );
      const countryCode = selectedCountryCodeRef.current || readStoredCountryCode();
      if (countryCode && !headers.has('X-Country-Code')) {
        headers.set('X-Country-Code', countryCode);
      }

      if (!(input instanceof URL) && typeof input !== 'string') {
        const request = new Request(input, {
          ...init,
          headers,
        });
        return originalFetch(request);
      }

      return originalFetch(input, {
        ...init,
        headers,
      });
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  const selectedCountry = useMemo(
    () => countries.find((country) => country.code === selectedCountryCode) || COUNTRIES.find((country) => country.code === selectedCountryCode),
    [countries, selectedCountryCode]
  );

  const handleSetCountry = (code: CountryCode) => {
    const normalized = String(code || '').trim().toUpperCase();
    if (!normalized) return;
    const nextCountry = countries.find((country) => country.code === normalized) || COUNTRIES.find((country) => country.code === normalized);
    if (!nextCountry) return;
    setSelectedCountryCode(nextCountry.code);
    persistCountry(nextCountry, nextCountry.code);
  };

  return (
    <CountryContext.Provider
      value={{
        selectedCountryCode,
        setSelectedCountryCode: handleSetCountry,
        selectedCountry,
        countries,
      }}
    >
      {children}
    </CountryContext.Provider>
  );
}

export function useCountry() {
  const context = useContext(CountryContext);
  if (context === undefined) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return context;
}
